import { Component, forwardRef, Input, OnInit } from '@angular/core';
import {FormBuilder, FormControl, FormGroup, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatOptionModule} from '@angular/material/core';
import {MatSelectModule} from '@angular/material/select';
import {MatInputModule} from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import {AbstractControlValueAccessor} from '../abstract-control-value-accessor';
import { Address2ComponentValue, compareMatch, PlacesSearchResultFn, getStates, getStreetTypes, parseRawAddress, parseAddressComponentsToString, PlacesItem, PlacesDetailsFn } from '@portal-workspace/grow-shared-library';
import {MARK, Mark} from '@portal-workspace/grow-ui-library/mark';
import { CustomErrorStateMatcher, formControlErrorKeys, formControlErrorMessage, setupUntilDestroy } from '../component-utils';
import { delay, distinctUntilChanged, tap, map } from 'rxjs/operators';
import {UntilDestroy} from '@ngneat/until-destroy';
import { combineLatest, Subscription, Observable, of } from 'rxjs';
import { AutocompleteDropdownComponent } from '../common/autocomplete-dropdown/autocomplete-dropdown.component';

@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  selector: 'custom-address',
  standalone: true,
  templateUrl: './custom-address.component.html',
  styleUrls: ['./custom-address.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CustomAddressComponent), multi: true},
    { provide: MARK, useExisting: forwardRef(() => CustomAddressComponent)},
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatOptionModule,
    MatSelectModule,
    MatAutocompleteModule,
    AutocompleteDropdownComponent
],
})
export class CustomAddressComponent extends AbstractControlValueAccessor<Address2ComponentValue> implements OnInit, Mark {

  subscriptions: Subscription[] = [];

  @Input({required: false}) title: string = 'Business address';
  @Input({required: false}) localOnly: boolean = true;
  @Input({required: false}) types: ('establishment' | 'address' | 'geocode')[] = ['address'];
  @Input({ required: true }) placesDetailsFn: PlacesDetailsFn | null = null;
  @Input({ required: true }) placesSearchResultFn: PlacesSearchResultFn | null = null;

  streetTypes = getStreetTypes();
  states = getStates();
  errorStateMatcher = new CustomErrorStateMatcher();
  errorKeys = formControlErrorKeys;
  errorMessage = formControlErrorMessage;

  // Add properties for the autocomplete dropdown
  addressDisplayWithFn: (item: PlacesItem | string | null) => string;
  fetchPlacesBySearchFn: () => Observable<PlacesItem[]>;

  formGroup: FormGroup<{
    address: FormControl<string | null>,
    UnitNumber: FormControl<string | null>,
    StreetNumber: FormControl<string | null>,
    StreetName: FormControl<string | null>,
    StreetType: FormControl<string | null>,
    Suburb: FormControl<string | null>,
    State: FormControl<string | null>,
    Postcode: FormControl<string | null>,
  }>;
  formControlAddress: FormControl<string | null>;
  formControlUnitNumber: FormControl<string | null>;
  formControlStreetNumber: FormControl<string | null>;
  formControlStreetName: FormControl<string | null>;
  formControlStreetType: FormControl<string | null>;
  formControlSuburb: FormControl<string | null>;
  formControlState: FormControl<string | null>;
  formControlPostcode: FormControl<string | null>;
  streetTypeCompareWith: (a: string, b: string) => boolean;
  stateCompareWith: (a: string, b: string) => boolean;

  inputPattern = /^(?!\s)[a-zA-Z0-9_'\s\-/,-]+$/;

  selectedIndex = -1;

  constructor(private formBuilder: FormBuilder) {
    super();
    this.streetTypeCompareWith = (a: string, b: string) => {
      const r = ((a ?? '').toLowerCase().trim() === (b ?? '').toLowerCase().trim());
      return r;
    };
    this.stateCompareWith = (a: string, b: string) => {
      return ((a ?? '').toLowerCase().trim() === (b ?? '').toLowerCase().trim());
    }
    this.formControlAddress = formBuilder.control(null, [Validators.required]);
    this.formControlUnitNumber = formBuilder.control(null,Validators.pattern(this.inputPattern));
    this.formControlStreetNumber = formBuilder.control(null, [Validators.required,Validators.pattern(this.inputPattern)]);
    this.formControlStreetName = formBuilder.control(null, [Validators.required,Validators.pattern(this.inputPattern)]);
    this.formControlStreetType = formBuilder.control(null, [Validators.required]);
    this.formControlSuburb = formBuilder.control(null, [Validators.required,Validators.pattern(this.inputPattern)]);
    this.formControlState = formBuilder.control(null, [Validators.required]);
    this.formControlPostcode = formBuilder.control(null, [Validators.required, Validators.pattern('^[0-9]{4}$')]);
    this.formGroup = formBuilder.group({
      address: this.formControlAddress,
      UnitNumber: this.formControlUnitNumber,
      StreetNumber: this.formControlStreetNumber,
      StreetName: this.formControlStreetName,
      StreetType: this.formControlStreetType,
      Suburb: this.formControlSuburb,
      State: this.formControlState,
      Postcode: this.formControlPostcode,
    });

    // Initialize the display function for autocomplete dropdown
    this.addressDisplayWithFn = (item: PlacesItem | string | null) => {
      if (typeof item === 'string' || item == null) {
        return item ?? '';
      }
      return item.description ?? '';
    };
    this.fetchPlacesBySearchFn = () => of([]);
  }

  override setDisabledState(isDisabled: boolean) {
    super.setDisabledState(isDisabled);
    if (isDisabled) {
      this.formControlAddress.disable();
      this.formControlUnitNumber.disable();
      this.formControlStreetNumber.disable();
      this.formControlStreetName.disable();
      this.formControlStreetType.disable();
      this.formControlSuburb.disable();
      this.formControlState.disable();
      this.formControlPostcode.disable();
    } else {
      this.formControlAddress.enable();
      this.formControlUnitNumber.enable();
      this.formControlStreetNumber.enable();
      this.formControlStreetName.enable();
      this.formControlStreetType.enable();
      this.formControlSuburb.enable();
      this.formControlState.enable();
      this.formControlPostcode.enable();
    }
  }

  doWriteValue(v: Address2ComponentValue | null | undefined): void {
    if (!v) {
      return;
    }

    // Populate all individual form controls. Angular will update the autocomplete input automatically.
    this.formControlAddress.setValue(v.address);
    this.formControlUnitNumber.setValue(v.UnitNumber ?? '');
    this.formControlStreetNumber.setValue(v.StreetNumber);
    this.formControlStreetName.setValue(v.StreetName);
    this.formControlStreetType.setValue(v.StreetType);
    this.formControlPostcode.setValue(v.Postcode);
    this.formControlSuburb.setValue(v.Suburb);
    this.formControlState.setValue(v.State);
  }

  mark(): void {
    this.formGroup.markAllAsTouched();
  }


  async ngOnInit() {
    setupUntilDestroy(this);

    // formgroup changes
    this.subscriptions.push(this.formGroup.valueChanges.pipe(
      delay(0),
      distinctUntilChanged(compareMatch),
      tap(r => {
        if (r && (this.formGroup.valid || this.formGroup.disabled)) {
          const address = parseAddressComponentsToString(
            this.formControlUnitNumber.value,
            this.formControlStreetNumber.value,
            this.formControlStreetName.value,
            this.formControlStreetType.value,
            this.formControlSuburb.value,
            this.formControlState.value,
            this.formControlPostcode.value,
          );
          this.propagateChange({
            address: address ?? '',
            UnitNumber: this.formControlUnitNumber.value ?? '',
            StreetNumber: this.formControlStreetNumber.value ?? '',
            StreetName: this.formControlStreetName.value ?? '',
            StreetType: this.formControlStreetType.value ?? '',
            Suburb: this.formControlSuburb.value ?? '',
            State: this.formControlState.value ?? '',
            Postcode: this.formControlPostcode.value ?? '',
          });
        } else {
          this.propagateChange(null);
        }
      })
    ).subscribe());

    this.subscriptions.push(combineLatest([
      this.formControlUnitNumber.valueChanges,
      this.formControlStreetNumber.valueChanges,
      this.formControlStreetName.valueChanges,
      this.formControlStreetType.valueChanges,
      this.formControlSuburb.valueChanges,
      this.formControlState.valueChanges,
      this.formControlPostcode.valueChanges,
    ]).pipe(tap(r => {
      const address = parseAddressComponentsToString(
        r[0], // this.formControlUnitNumber.value,
        r[1], // this.formControlStreetNumber.value,
        r[2], // this.formControlStreetName.value,
        r[3], // this.formControlStreetType.value,
        r[4], // this.formControlSuburb.value,
        r[5], // this.formControlState.value,
        r[6], // this.formControlPostcode.value,
      );
      this.formControlAddress.setValue(address);
    })).subscribe());


  }

  onAddressInput(event: any): void {
    this.fetchPlacesBySearchFn = () => this.fetchPlacesDataBySearchTerm(event.target.value);
  }

  private parseAndPopulate(rawAddress: string) {
    const parsedAddress = parseRawAddress(rawAddress);
    if (parsedAddress) {
      this.formControlUnitNumber.setValue(parsedAddress.unit_number ?? null);
      this.formControlStreetName.setValue(parsedAddress.street_name);
      this.formControlStreetType.setValue(parsedAddress.street_type);
      this.formControlStreetNumber.setValue(parsedAddress.street_number);
      this.formControlState.setValue(parsedAddress.region);
      this.formControlSuburb.setValue(parsedAddress.locality);
      this.formControlPostcode.setValue(parsedAddress.postal_code);
    }
  }


  async onPlaceSelected(place: PlacesItem): Promise<void> {
    if (!place.place_id) {
      return;
    }

    try {
      this.placesDetailsFn?.(place.place_id).subscribe((result) => {

        if (result.status == 'OK' && result.result) {
          this.parseAndPopulate(result.result.formatted_address);
        } else {
          console.error('Place details request failed:', result.status);
        }
      });
      this.selectedIndex = -1;

    } catch (error) {
      console.error('Error getting place details:', error);
    }
  }

  // Method to handle customer selection from autocomplete dropdown
  onPlacesSelected(place: PlacesItem): void {
    this.onPlaceSelected(place);
  }

  // Method to return Observable for autocomplete-dropdown component
  fetchPlacesDataBySearchTerm(searchTerm: string): Observable<PlacesItem[]> {
    if (!this.placesSearchResultFn || !searchTerm || searchTerm.trim().length < 2 && (this.formControlAddress.value == null || this.formControlAddress.value != searchTerm)) {
      return of([]);
    }

    return this.placesSearchResultFn!(searchTerm).pipe(
      map((result) => {
        if (result && result.status === 'OK' && result.predictions) {
          return result.predictions;
        }
        return [];
      })
    );
  }

}
